import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plane, Bus, MapPin, ArrowRight, Route, Sparkles, Mountain, FileText } from "lucide-react";

const AIRPORT_IMG = "https://images.pexels.com/photos/1730814/pexels-photo-1730814.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const groupShuttle = [
  { pax: 1, each: 45 }, { pax: 2, each: 30 }, { pax: 3, each: 25 },
  { pax: 4, each: 20 }, { pax: 5, each: 17.5 }, { pax: 6, each: 15 },
];

const privateCar = [
  { k: "halifax_downtown", label: "Halifax Downtown & surrounding areas", price3: 60, price6: 70 },
  { k: "dartmouth", label: "Downtown Dartmouth area", price3: 60, price6: 70 },
  { k: "lower_sackville", label: "Lower Sackville area", price3: 55, price6: 62 },
  { k: "middle_sackville", label: "Middle Sackville", price3: 65, price6: 75 },
  { k: "upper_sackville", label: "Upper Sackville", price3: 70, price6: 80 },
  { k: "spryfield", label: "Spryfield area", price3: 65, price6: 75 },
];

const quoteServices = [
  { icon: MapPin, title: "Nearby & Local Area Trips", desc: "Rides around Halifax and the surrounding communities.", svc: "Nearby & Local Area Trip" },
  { icon: Route, title: "Long Distance Trips", desc: "Travel across Nova Scotia, New Brunswick & PEI.", svc: "Long Distance Trip" },
  { icon: Mountain, title: "Charter Touring Service", desc: "Historic and scenic site tours — Peggy's Cove, Lunenburg, Annapolis Valley & more.", svc: "Charter Touring Service" },
  { icon: Sparkles, title: "Events, Weddings & Charters", desc: "Special occasions, corporate accounts & hourly hire.", svc: "Special Event" },
];

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, delay: d, ease: "easeOut" },
});

export default function Services() {
  return (
    <div>
      <section className="max-w-[1720px] mx-auto px-5 sm:px-10 pt-14 sm:pt-20 pb-14">
        <span className="text-sm uppercase tracking-[0.2em] text-gold">Airport Transfer</span>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tighter max-w-3xl">
          Airport Shuttle Transfer
        </h1>
        <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
          Reliable, upfront-priced rides to &amp; from Halifax Stanfield International Airport. Book instantly
          below — or request a custom quote for tours, long-distance trips, events and more.
        </p>
      </section>

      {/* AIRPORT TRANSFER */}
      <section className="max-w-[1720px] mx-auto px-5 sm:px-10 pb-8">
        <div className="grid gap-6 lg:grid-cols-12 items-stretch">
          <motion.div {...fade(0)} className="lg:col-span-4 relative rounded-sm overflow-hidden border border-gold/20 min-h-[300px]">
            <img src={AIRPORT_IMG} alt="Airport terminal" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-transparent" />
            <div className="relative z-10 p-8 h-full flex flex-col justify-end">
              <Plane className="h-8 w-8 text-gold mb-3" />
              <h2 className="font-heading text-2xl font-bold text-white">Airport Transfer</h2>
              <p className="text-slate-300 text-sm mt-2">To &amp; from Halifax Stanfield International Airport.</p>
            </div>
          </motion.div>

          {/* Shared Shuttle */}
          <motion.div {...fade(0.16)} className="lg:col-span-4 rounded-sm bg-navy-light border border-gold/20 p-8">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold mb-2">
              <Bus className="h-4 w-4" /> Shared Airport Shuttle
            </div>
            <h3 className="font-heading text-xl font-bold text-white mb-1">Flat $45 per passenger</h3>
            <p className="text-slate-400 text-sm mb-2">Riders from different locations share the shuttle — a simple flat rate of $45 each, to &amp; from Halifax Stanfield.</p>
            <Link to="/book?service=shared_shuttle" data-testid="book-shuttle" className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-sm font-semibold text-navy transition-transform duration-200 hover:-translate-y-0.5 hover:bg-gold-light">
              Book shared shuttle <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>

          {/* Private Group Airport Shuttle */}
          <motion.div {...fade(0.24)} className="lg:col-span-4 rounded-sm bg-navy-light border border-gold/20 p-8" data-testid="private-group-shuttle-card">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold mb-2"><Bus className="h-4 w-4" /> Private Group Airport Shuttle</div>
            <h3 className="font-heading text-xl font-bold text-white mb-1">One address, whole group</h3>
            <p className="text-slate-400 text-sm mb-5">Same pickup for the whole group — the more passengers, the less each person pays.</p>
            <div className="grid grid-cols-3 gap-3">
              {groupShuttle.map((r) => (
                <div key={r.pax} className="rounded-sm border border-gold/15 bg-navy p-3 text-center" data-testid={`group-shuttle-${r.pax}`}>
                  <div className="text-slate-400 text-[11px] uppercase tracking-wider">{r.pax} {r.pax === 1 ? "person" : "people"}</div>
                  <div className="font-heading text-xl font-bold text-gold mt-1">${r.each % 1 === 0 ? r.each : r.each.toFixed(2)}</div>
                  <div className="text-slate-500 text-[10px] mt-0.5">per person</div>
                </div>
              ))}
            </div>
            <Link to="/book?service=private_group_shuttle" data-testid="book-group-shuttle" className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-sm font-semibold text-navy transition-transform duration-200 hover:-translate-y-0.5 hover:bg-gold-light">
              Book private group <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* PRIVATE CAR DESTINATIONS */}
      <section className="max-w-[1720px] mx-auto px-5 sm:px-10 pb-8">
        <div className="rounded-sm bg-navy-light border border-gold/20 p-8 sm:p-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-gold mb-2"><Plane className="h-4 w-4" /> Private Airport Car</div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">Fares to/from Halifax Airport</h2>
              <p className="mt-3 text-slate-300 max-w-xl">Private vehicle, door to door. Prices shown are <span className="text-white font-semibold">up to 3 passengers</span> / <span className="text-white font-semibold">up to 6 passengers</span>.</p>
            </div>
            <Link to="/book?service=private_car" data-testid="book-private-car-2" className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-navy transition-transform duration-200 hover:-translate-y-0.5 hover:bg-gold-light shrink-0">
              Book private car <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-x-10 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
            {privateCar.map((t) => (
              <Link key={t.k} to="/book?service=private_car" data-testid={`privatecar-row-${t.k}`}
                className="group flex items-center justify-between border-b border-gold/10 py-3.5 transition-colors duration-200 hover:border-gold/40">
                <span className="text-slate-200 group-hover:text-white flex items-center gap-2"><MapPin className="h-4 w-4 text-gold/70" /> {t.label}</span>
                <span className="font-heading text-lg font-bold text-gold">${t.price3}<span className="text-slate-500 font-normal text-sm"> / </span>${t.price6}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* REQUEST A QUOTE — everything else */}
      <section className="bg-navy-light border-y border-gold/10 relative grain">
        <div className="max-w-[1720px] mx-auto px-5 sm:px-10 py-14 sm:py-20 relative z-10">
          <motion.div {...fade(0)} className="mb-10 max-w-2xl">
            <span className="text-sm uppercase tracking-[0.2em] text-gold">Custom trips</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">Need something else? Request a quote</h2>
            <p className="mt-4 text-slate-300">For local trips, long-distance journeys, guided tours, weddings, events and charters, tell us what you need and we'll send you a tailored price — no obligation.</p>
          </motion.div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-10">
            {quoteServices.map((s, i) => (
              <motion.div key={s.title} {...fade(i * 0.08)}>
                <Link to={`/quote?service=${encodeURIComponent(s.svc)}`} data-testid={`quote-service-card-${i}`} className="group h-full rounded-sm bg-navy border border-gold/20 p-7 flex flex-col transition-transform duration-200 hover:-translate-y-1.5 hover:border-gold/50">
                  <div className="grid place-items-center h-12 w-12 rounded-sm bg-gold/10 text-gold mb-5 transition-colors duration-200 group-hover:bg-gold group-hover:text-navy">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-white">{s.title}</h3>
                  <p className="text-slate-400 text-sm mt-2 flex-1">{s.desc}</p>
                </Link>
              </motion.div>
            ))}
          </div>
          <Link to="/quote" data-testid="services-request-quote" className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-semibold text-navy transition-transform duration-200 hover:-translate-y-0.5 hover:bg-gold-light">
            <FileText className="h-5 w-5" /> Request a quote
          </Link>
        </div>
      </section>
    </div>
  );
}
